import { faEye } from '@fortawesome/free-regular-svg-icons';
import { faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import DialogModal from 'components/modals/DialogModal';
import TicketAppointmentEditModal from 'components/modals/TicketAppointmentEditModal';
import TicketAppointmentRegistrationModal from 'components/modals/TicketAppointmentRegistrationModal';
import TicketAppointmentViewModal from 'components/modals/TicketAppointmentViewModal';
import Pagination from 'components/pagination';
import useApi from 'helpers/api';
import { formatTime } from 'helpers/utils';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useNotification from 'hooks/useNotification';
import useTicketsAppointmentsTable from 'hooks/useTicketsAppointmentsTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

const TicketAppointments = ({
  ticketId,
  ticketStatus,
  refreshLogs,
  refreshData,
  onCountChange
}) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const api = useApi();
  const formatDateTime = useFormatDateTime();
  const [appointments, setAppointments] = useState({ content: [] });
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({
    page: 0,
    size: 10
  });

  const [showModalRegister, setShowModalRegister] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalView, setShowModalView] = useState(false);
  const [showDialogConfirm, setShowDialogConfirm] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState({});

  const hasTicketId = ticketId != undefined;
  const isEmpty =
    hasTicketId && appointments.content && appointments.content.length === 0;

  useEffect(() => {
    if (hasTicketId) {
      list();
    }
  }, [ticketId, filters]);

  useEffect(() => {
    if (appointments && onCountChange) {
      const totalCount =
        appointments.totalElements !== undefined
          ? appointments.totalElements
          : 0;
      onCountChange(totalCount);
    }
  }, [appointments]);

  const isTicketCompleted =
    ticketStatus === 'COMPLETED' ||
    ticketStatus === 'CLOSED' ||
    ticketStatus?.deleted;
  const columns = [
    {
      accessorKey: 'typeService',
      header: 'Tipo',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { typeService } = rowData.row.original;
        return <p>{typeService && typeService.description}</p>;
      }
    },
    {
      accessorKey: 'date',
      header: 'Data',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { date } = rowData.row.original;
        return <p>{formatDateTime(date)}</p>;
      }
    },
    {
      accessorKey: 'timeSpent',
      header: 'Horas trabalhadas',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { timeSpent } = rowData.row.original;
        return <p>{formatTime(timeSpent)}</p>;
      }
    },
    {
      accessorKey: 'agent',
      header: 'Agente',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { user } = rowData.row.original;
        return <p>{user.firstName}</p>;
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <>
            <Button variant="link" size="sm" onClick={() => handleView(item)}>
              <FontAwesomeIcon icon={faEye} size="sm" />
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleEdit(item)}
              disabled={isTicketCompleted}
            >
              <FontAwesomeIcon icon={faPen} size="sm" />
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleDelete(item)}
              disabled={isTicketCompleted}
            >
              <FontAwesomeIcon icon={faTrash} size="sm" />
            </Button>
          </>
        );
      }
    }
  ];

  const list = async () => {
    if (hasTicketId) {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await api.appointments.list(ticketId, queryParams);
      setAppointments(response);

      const summaryData = await api.appointments.summary(ticketId);
      setSummary(summaryData);

      if (onCountChange && summaryData && summaryData.count !== undefined) {
        onCountChange(summaryData.count);
      }
    }
  };

  const deleteAppointment = async response => {
    if (response && hasTicketId) {
      const request = await api.appointments.delete(
        ticketId,
        selectedAppointment.id
      );
      if (request.ok) {
        showSuccessNotification('Apontamento excluído com sucesso');
        list();
        refreshLogs();
      } else {
        showErrorNotification(request.userMessage);
      }
      handleCloseDialog();
    }
  };

  const handleRegister = () => {
    setSelectedAppointment({});
    setShowModalRegister(true);
  };

  const handleView = item => {
    setSelectedAppointment(item);
    setShowModalView(true);
  };

  const handleEdit = item => {
    setSelectedAppointment(item);
    setShowModalEdit(true);
  };

  const handleDelete = item => {
    setSelectedAppointment(item);
    setShowDialogConfirm(true);
  };

  const handleCloseModalRegister = () => {
    setShowModalRegister(false);
  };

  const handleCloseDialog = () => {
    setShowDialogConfirm(false);
  };

  const table = useTicketsAppointmentsTable({
    data: appointments.content,
    columns
  });

  return (
    <>
      <div>
        <header className="mb-3">
          <Flex
            className={'w-100'}
            justifyContent="between"
            alignItems="center"
          >
            <Flex alignItems={'center'} className="flex-grow-1 flex-wrap">
              <h3 className="m-0 text-nowrap me-3">Horas apontadas</h3>

              <div className="d-flex align-items-center fs--1 text-600">
                <span className="fw-semi-bold text-nowrap">
                  Total: {summary.totalHours || '00:00'} ({summary.count || 0})
                </span>
                <span className="mx-2">|</span>
                <span className="text-nowrap">
                  Int: {summary.internalHours || '00:00'}
                </span>
                <span className="mx-2">|</span>
                <span className="text-nowrap">
                  Ext: {summary.externalHours || '00:00'}
                </span>
              </div>
            </Flex>

            <div className="ms-3">
              <Button
                type="button"
                disabled={isTicketCompleted}
                variant="primary"
                size="sm"
                onClick={() => handleRegister()}
              >
                <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
                Novo
              </Button>
            </div>
          </Flex>
        </header>
        <div>
          <TicketAppointmentViewModal
            data={selectedAppointment}
            showModal={showModalView}
            handleClose={() => setShowModalView(false)}
          />
          <TicketAppointmentRegistrationModal
            showModal={showModalRegister}
            handleClose={handleCloseModalRegister}
            ticketId={ticketId}
            refreshList={() => list()}
            refreshLogs={() => refreshLogs()}
            refreshTicket={() => refreshData()}
          />
          <TicketAppointmentEditModal
            showModal={showModalEdit}
            handleClose={() => setShowModalEdit(false)}
            ticketId={ticketId}
            refreshList={() => list()}
            refreshLogs={() => refreshLogs()}
            data={selectedAppointment}
          />

          <DialogModal
            message="Tem certeza que deseja excluir este apontamento?"
            showModal={showDialogConfirm}
            handleClose={handleCloseDialog}
            response={deleteAppointment}
          />

          {isEmpty ? (
            <div style={{ minHeight: '300px' }}>
              <EmptyList imgSize={250} />
            </div>
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  table
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
              </AdvanceTableProvider>
              <Pagination
                data={appointments}
                filters={filters}
                setFilters={setFilters}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TicketAppointments;
